import { useSuspenseQuery } from '@apollo/client';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, TextField, Typography } from '@mui/material';
import Fuse from 'fuse.js';
import { useEffect, useState, type FC } from 'react';
import { List, type RowComponentProps } from 'react-window';
import { TabContent, TabProps } from '../common';
import AllAccessListItem from './AllAccessListItem';
import GET_ALL_DEVICE_ACCESS_FOR_USER, {
  UserDeviceAccessEntry,
} from './graphql/queries/getAllDeviceAccessForUser';

const AllAccessRow: FC<
  RowComponentProps<{
    entries: UserDeviceAccessEntry[];
  }>
> = ({ index, style, entries }) => {
  const entry = entries[index];
  return (
    <AllAccessListItem
      style={style}
      lastItem={index === entries.length - 1}
      deviceId={entry.device.id}
      deviceName={entry.device.profile?.name ?? ''}
      deviceOrganization={entry.device.profile?.organization ?? null}
      deviceIrrigationName={entry.device.profile?.irrigation?.name}
      direct={entry.direct}
      collections={entry.collections}
      visibleToUser={entry.visibleToUser}
      operationalState={entry.device.profile?.operationalState}
      deviceVisibleToClient={entry.device.visibleToClient}
      operationalStateVisibleToClient={entry.device.operationalStateVisibleToClient}
    />
  );
};

export const AllAccessTab: FC<Omit<TabProps, 'setIsDirty'>> = ({ userId }) => {
  const { _ } = useLingui();
  const { data } = useSuspenseQuery(GET_ALL_DEVICE_ACCESS_FOR_USER, {
    variables: { userId },
  });
  const [organizationIds, setOrganizationIds] = useState<number[]>([]);
  const [search, setSearch] = useState('');

  const allEntries = data.user.allDeviceAccess;

  const [filteredEntries, setFilteredEntries] = useState(allEntries);

  useEffect(() => {
    // Orden base: acceso directo primero, luego solo-colección; dentro de cada grupo, alfabético
    // por nombre (fallback deviceID). visibleToUser NO ordena — es solo señal visual (fila atenuada).
    let items = [...allEntries].sort((a, b) => {
      if (a.direct !== b.direct) return a.direct ? -1 : 1;
      const nameA = a.device.profile?.name || a.device.id;
      const nameB = b.device.profile?.name || b.device.id;
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base', numeric: true });
    });

    if (organizationIds.length) {
      const orgIdSet = new Set(organizationIds);
      items = items.filter(
        (entry) => entry.device.profile?.organization && orgIdSet.has(entry.device.profile.organization.id),
      );
    }

    if (search) {
      const fuse = new Fuse(items, { keys: ['device.profile.name', 'device.id'] });
      items = fuse.search(search).map(({ item }) => item);
    }

    setFilteredEntries(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, organizationIds, allEntries]);

  return (
    <TabContent>
      <Typography variant="h6" mb={2}>
        <Trans>Todos los dispositivos a los que tiene acceso el usuario</Trans>
      </Typography>
      <Box mb={1} display="flex" justifyContent="stretch" alignItems="center" gap={2}>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          label={_(msg`Buscar`)}
          sx={{ flex: 3 }}
        />
        <StandardAutocompleteIdsWithFallback
          sx={{ flex: 2 }}
          size="small"
          dataType="organizations"
          multiple
          multipleCheckboxes
          value={organizationIds}
          onChange={(_event, values) => setOrganizationIds(values)}
        />
        <Typography textAlign="right">
          <Trans>
            Con acceso a <b>{allEntries.length}</b> dispositivos
          </Trans>
        </Typography>
      </Box>
      <Box border={1} borderColor="divider" borderRadius={1} height={380}>
        <List
          rowCount={filteredEntries.length}
          rowHeight={56}
          rowComponent={AllAccessRow}
          rowProps={{ entries: filteredEntries }}
        />
      </Box>
    </TabContent>
  );
};
